import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { errorKey, errorTitleKey, isRefusal, requestIdOf } from '@/api/errors';
import { Button } from '@/components/ui/Button';
import { ICONS } from '@/components/ui/icons';
import { cn } from '@/lib/cn';

import { useOnASheet } from './on-a-sheet';

export interface ErrorStateProps {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
  /**
   * One line instead of a centred block, for a place where a block would break the layout it
   * sits in — a filter bar, a toolbar, a row of chips. The copy is identical; only the
   * arrangement changes, so there is still exactly one component that renders a failure.
   */
  inline?: boolean;
  className?: string;
}

/**
 * The only component that renders a failure. It renders `errors.code.*` from the machine-readable
 * code — never `error.message`, which is English and written for developers (§12.3).
 *
 * TWO THINGS IT WILL NOT DO ANY MORE.
 *
 * 1. OFFER "PRØV IGJEN" AS THE ONLY EXIT FROM A REFUSAL. A 403 is an answer, not a malfunction:
 *    the server has decided, and it will decide the same thing every time. A retry button on it
 *    is a button that cannot work, and pressing it is the only thing the box invited anyone to
 *    do. A refusal gets a way *out* — back to a page this person may actually read — and no
 *    retry at all. (`errorTitleKey` already stopped calling it "Noe gikk galt" for the same
 *    reason.)
 *
 * 2. PUT A UUID IN A SENTENCE. "Forespørsels-ID 019249a3-…-8f21" read as prose that happened to
 *    end in noise, and a reader looking for something to *do* had to parse a line that was never
 *    addressed to them. It is a technical value: it gets a label, its own line and monospace, so
 *    it can be read aloud to support digit by digit — which is the only thing it is for. 12px
 *    mono rather than the 13px body size for the same reason a table cell is not prose; the
 *    LABEL beside it is the sentence, and it is at body size (DESIGN.md §2).
 */
export function ErrorState({
  error,
  onRetry,
  compact = false,
  inline = false,
  className,
}: ErrorStateProps): ReactNode {
  const { t } = useTranslation();
  const Icon = ICONS.warning;
  const requestId = requestIdOf(error);
  const refused = isRefusal(error);
  /* På et ark: tonalt fyll uten kant. På siden: sitt eget ark (Glass 2, §16.3). */
  const sheet = useOnASheet();

  const description = <p className="max-w-sm text-sm text-muted">{t(errorKey(error))}</p>;

  /** A refusal is answered by leaving; anything else is answered by trying again. */
  const wayOn = refused ? (
    <Link
      to="/"
      className={cn(
        'hit-target control-tonal inline-flex h-8 items-center rounded-[var(--radius-control)] px-3.5',
        'text-sm font-medium text-default ring-focus',
        'transition-colors duration-[var(--duration-fast)]',
      )}
    >
      {t('errors.forbidden.exit')}
    </Link>
  ) : onRetry ? (
    <Button size="sm" onClick={onRetry}>
      {t('common.actions.retry')}
    </Button>
  ) : null;

  if (inline) {
    return (
      <div
        role="alert"
        ref={sheet.ref}
        className={cn(
          'flex flex-wrap items-center gap-2 px-2.5 py-1.5',
          sheet.onSheet ? 'rounded-[var(--radius-inner)] bg-[var(--fill-tonal)]' : 'glass-content',
          className,
        )}
      >
        <Icon aria-hidden className="size-3.5 shrink-0 text-[color:var(--color-danger)]" />
        {description}
        {wayOn}
      </div>
    );
  }

  return (
    <div
      role="alert"
      ref={sheet.ref}
      className={cn(
        'flex flex-col items-center gap-2 text-center',
        !sheet.onSheet && 'glass-content',
        compact ? 'px-3 py-5' : 'px-6 py-12',
        className,
      )}
    >
      <Icon aria-hidden className="size-4 text-[color:var(--color-danger)]" />
      <p className="text-xs font-medium text-default">{t(errorTitleKey(error))}</p>
      {description}
      {requestId ? (
        <p className="flex flex-wrap items-baseline justify-center gap-1.5 text-sm text-subtle">
          {t('errors.requestId')}
          <span className="numeric font-mono text-xs break-all text-muted">{requestId}</span>
        </p>
      ) : null}
      {wayOn ? <div className="mt-1">{wayOn}</div> : null}
    </div>
  );
}
